import { supabase } from '../lib/supabase';

export const notificationService = {
  // Request browser notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  },

  // Show browser notification
  showNotification(title, options = {}) {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });

      // Auto-close after 10 seconds
      setTimeout(() => notification?.close(), 10000);

      return notification;
    }
  },

  // Send email notification via Edge Function
  async sendEmailNotification(formData) {
    try {
      // Get session if available (for admin actions), but don't require it
      const { data: { session } } = await supabase?.auth?.getSession();
      
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add authorization header only if session exists
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session?.access_token}`;
      }

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-form-notification`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(formData)
        }
      );

      if (!response?.ok) {
        const error = await response?.json();
        throw new Error(error.error || 'Failed to send email notification');
      }

      return await response?.json();
    } catch (error) {
      console.error('Email notification error:', error);
      throw error;
    }
  },

  // Subscribe to real-time form submissions
  subscribeToFormSubmissions(callback) {
    const channel = supabase
      ?.channel('form_submissions_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'form_submissions'
        },
        (payload) => {
          callback(payload?.new);
        }
      )
      ?.subscribe();

    return channel;
  },

  // Subscribe to form submission updates (status changes)
  subscribeToFormUpdates(callback) {
    const channel = supabase
      ?.channel('form_updates_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'form_submissions'
        },
        (payload) => {
          callback(payload?.new);
        }
      )
      ?.subscribe();

    return channel;
  },

  // Subscribe to email sequence tracking updates
  subscribeToEmailTracking(callback) {
    const channel = supabase
      ?.channel('email_tracking_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_sequence_tracking'
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return channel;
  },

  // Subscribe to dashboard metrics updates
  subscribeToMetrics(callback) {
    const channel = supabase
      ?.channel('metrics_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'dashboard_metrics'
        },
        (payload) => {
          callback(payload?.new);
        }
      )
      ?.subscribe();

    return channel;
  },

  // Unsubscribe from real-time updates
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  // Format notification message based on form type
  formatNotificationMessage(submission) {
    const formTypeLabels = {
      contact: 'Contact Form',
      consultation: 'Consultation Booking',
      partnership: 'Partnership Inquiry'
    };

    return {
      title: `New ${formTypeLabels?.[submission?.form_type] || 'Form'} Submission`,
      body: `From: ${submission?.name} (${submission?.email})`,
      tag: submission?.id,
      data: submission
    };
  },

  async sendInquiryNotification(formType, formData) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-inquiry-notification', {
        body: {
          formType,
          formData
        }
      });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error sending inquiry notification:', error);
      throw new Error(error?.message || 'Failed to send notification');
    }
  }
};